// src/contexts/session-context.js
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
} from "react";
import { createHttpClient } from "../api/client";

export const SESSION_NAME = "bpp_token";

const SessionContext = createContext(null);

function readToken() {
    return localStorage.getItem(SESSION_NAME) || "";
}

// ---- LINE in-app browser detection ----
function isLineInAppBrowser() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    // UA ตัวอย่างมักมี "Line/..." หรือ "LIFF"
    return /Line\/|LIFF/i.test(ua);
}

function isIOS() {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}
function isAndroid() {
    if (typeof navigator === "undefined") return false;
    return /Android/i.test(navigator.userAgent || "");
}

// ---- Gate UI ----
function OpenInExternalBrowserGate() {
    const [copied, setCopied] = useState(false);

    const url = useMemo(() => {
        if (typeof window === "undefined") return "";
        return window.location.href;
    }, []);

    const openExternal = useCallback(() => {
        // 1) ถ้ามี LIFF ให้ใช้วิธีนี้ดีที่สุด (external:true)
        try {
            const liff = window?.liff;
            if (liff && typeof liff.openWindow === "function") {
                liff.openWindow({ url, external: true });
                return;
            }
        } catch {
            // ignore
        }

        // 2) Android: ลอง intent ไป Chrome (บางเครื่อง/บาง policy อาจบล็อก)
        if (isAndroid()) {
            try {
                const { protocol, host, pathname, search, hash } = window.location;
                const scheme = protocol.replace(":", ""); // http / https
                const intentUrl =
                    `intent://${host}${pathname}${search}${hash}` +
                    `#Intent;scheme=${scheme};package=com.android.chrome;end`;
                window.location.href = intentUrl;
                return;
            } catch {
                // fallback ต่อไป
            }
        }

        // 3) iOS: บังคับออก Safari ตรงๆ ไม่ได้ 100% → ลองเปิดแท็บใหม่ก่อน
        try {
            window.open(url, "_blank", "noopener,noreferrer");
        } catch {
            // ignore
        }

        // 4) fallback สุดท้าย: เปลี่ยน location (อาจยังอยู่ใน in-app)
        try {
            window.location.href = url;
        } catch {
            // ignore
        }
    }, [url]);

    const copyLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            // fallback เก่า
            const ta = document.createElement("textarea");
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        }
    }, [url]);

    return (
        <div className="fixed inset-0 z-[9999] bg-white grid place-items-center px-5">
            <div className="w-full max-w-[420px] rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[18px] font-bold text-slate-900 text-center">
                    กรุณาเปิดในเบราว์เซอร์
                </div>

                <div className="mt-2 text-[13px] text-slate-600 leading-5 text-center">
                    ตอนนี้คุณกำลังเปิดผ่าน <b>LINE In-App Browser</b> ซึ่งอาจทำให้ล็อกอิน/คุกกี้ทำงานไม่ครบ
                    <br />
                    กดปุ่มด้านล่างเพื่อเปิดใน <b>Safari / Chrome</b>
                </div>

                <button
                    type="button"
                    onClick={openExternal}
                    className="mt-5 w-full h-12 rounded-xl bg-[#3E6F5E] text-white font-bold active:scale-[0.99]"
                >
                    เปิดในเบราว์เซอร์
                </button>

                <button
                    type="button"
                    onClick={copyLink}
                    className="mt-3 w-full h-12 rounded-xl border-2 border-slate-300 bg-white text-[#4B5563] font-bold"
                >
                    {copied ? "คัดลอกลิงก์แล้ว" : "คัดลอกลิงก์"}
                </button>

                {isIOS() && (
                    <div className="mt-4 text-[12px] text-slate-500 leading-5">
                        iOS อาจไม่สามารถเด้งออก Safari อัตโนมัติได้ในบางกรณี:
                        <br />• ให้กด “…” ใน LINE แล้วเลือก “เปิดใน Safari/Browser”
                        <br />• หรือคัดลอกลิงก์แล้วไปวางใน Safari/Chrome
                    </div>
                )}
            </div>
        </div>
    );
}

export function SessionProvider({ children }) {
    const [session, setSession] = useState({
        isAuthenticated: false,
        user: null,
        loading: true,
        token: readToken(), // ✅ เก็บ token ใน context ด้วย
    });

    const http = useMemo(() => createHttpClient(), []);

    // ✅ กันยิงซ้ำใน React 18 StrictMode (dev)
    const didInitRef = useRef(false);

    const refreshProfile = useCallback(async () => {
        const token = readToken();

        // ✅ ถ้าไม่มี token → ไม่ต้องยิง profile
        if (!token) {
            setSession({ isAuthenticated: false, user: null, loading: false, token: "" });
            localStorage.removeItem("USER_ID");
            return;
        }

        // เริ่มโหลดใหม่ โดยคง token ไว้
        setSession((s) => ({ ...s, loading: true, token }));

        try {
            const res = await http.get("/api/auth/profile");
            const user = res?.data?.user;

            if (user?.id) {
                setSession({ isAuthenticated: true, user, loading: false, token });
                localStorage.setItem("USER_ID", String(user.id));
            } else {
                setSession({ isAuthenticated: false, user: null, loading: false, token });
                localStorage.removeItem("USER_ID");
            }
        } catch (err) {
            setSession({ isAuthenticated: false, user: null, loading: false, token });
            localStorage.removeItem("USER_ID");
        }
    }, [http]);

    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;
        refreshProfile();
    }, [refreshProfile]);

    const value = useMemo(
        () => ({ session, setSession, refreshProfile }),
        [session, refreshProfile]
    );

    // ✅ ถ้าเปิดใน LINE in-app browser → บังคับให้ผู้ใช้กดเปิดใน browser ภายนอกก่อน
    const inLine = useMemo(() => isLineInAppBrowser(), []);
    if (inLine) {
        return <OpenInExternalBrowserGate />;
    }

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionContext() {
    const ctx = useContext(SessionContext);
    if (!ctx) throw new Error("useSessionContext must be used within SessionProvider");
    return ctx; // { session, setSession, refreshProfile }
}